import { TestMetaProfile, ExitQuestion, SupportedInputTypeEnum, SupportedInputTypeE } from "@/testmeta.types";
import { useProfile } from '$/contexts/profile.ctx.tsx';
import { Card, Group, Text, Button, Divider, Collapse, Stack, Checkbox, Select, List, TextInput, TagsInput, Box } from "@mantine/core";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { useState, useEffect } from "react";
import { useMap, useInputState, useToggle } from '@mantine/hooks';
import Preview from '@/componets/Preview';
import { DynamicComponent, DynamicQuestionProp } from '@/componets/DynamicComponent';
import { DynamicForm } from '@/componets/DynamicComponent/dynamic.form';
import { Draggal } from '@/componets/Draggal';
import { MetaCompBlks } from "@/componets/DynamicComponent/meta.components";
import { showNotification } from "@mantine/notifications";
import { DemoRoot } from '@/componets/Preview/DemoRoot';
import { DemoArea } from '@/componets/Preview/DemoArea';


export default function TestMetaProfileCard() {
    const { testMetaProfile, updateTestMetaProfile } = useProfile();
    const [showTestMetaProfile, setShowTestMetaProfile] = useState(false);
    const [allNonTestQuestionsToEnd, setAllNonTestQuestionsToEnd] = useState(false);
    const currentTestMetaProfile = useMap(Object.entries(testMetaProfile || {}));
    const [showPreview, togglePreview] = useToggle([1, 2] as const);

    // New/Edit a Exit Question Inputs
    const [selectedExitQuestion, setSelectedExitQuestion] = useState<number>(-1); // -1 for new
    const [newQuestLabel, setNewQuestLabel] = useInputState('');
    const [newQuestSecondaryLabel, setNewQuestSecondaryLabel] = useInputState('');
    const [newQuestOptions, setNewQuestOptions] = useInputState<string[]>([]);
    const [newQuestRestrict, setNewQuestRestrict] = useInputState('');
    const [newQuestQType, setNewQuestQType] = useInputState('');

    useEffect(() => {
        if (testMetaProfile) {
            Object.entries(testMetaProfile).forEach(([k, v]) => {
                currentTestMetaProfile.set(k, v);
            })
        }
    }, [testMetaProfile]);

    useEffect(() => {
        if (selectedExitQuestion === -1) {
            setNewQuestLabel('');
            setNewQuestSecondaryLabel('');
            setNewQuestOptions([]);
            setNewQuestRestrict('');
            setNewQuestQType('');
        } else {
            const eqs = currentTestMetaProfile.get('exitQuestions') as ExitQuestion[];
            if (eqs && eqs[selectedExitQuestion]) {
                const eq = eqs[selectedExitQuestion];
                setNewQuestLabel(eq.label);
                setNewQuestSecondaryLabel(eq.secondaryLabel || '');
                setNewQuestOptions(eq.options || []);
                setNewQuestRestrict(eq.restrict ? 'true' : 'false');
                setNewQuestQType(eq.qType);
            } else console.warn('Exit question not found', selectedExitQuestion);
        }
    }, [selectedExitQuestion]);
    const deleteExitQuestion = (index: number) => {
        const eqs = currentTestMetaProfile.get('exitQuestions') as ExitQuestion[] || [];
        eqs.splice(index, 1);
        currentTestMetaProfile.set('exitQuestions', eqs);
    }
    const exitQuestionUiList = () => {
        const eqs = currentTestMetaProfile.get('exitQuestions') as ExitQuestion[] || [];
        if (!eqs) return null;
        return (<Draggal items={eqs} clicked={setSelectedExitQuestion} selected={selectedExitQuestion} deleteFn={deleteExitQuestion} />)
    };



    const addExitQuestion = () => {
        const eqs = currentTestMetaProfile.get('exitQuestions') as ExitQuestion[] || [];
        if (selectedExitQuestion === -1) {
            eqs.push({
                label: newQuestLabel,
                key: Date.now(),
                secondaryLabel: newQuestSecondaryLabel,
                restrict: newQuestRestrict === 'true',
                qType: newQuestQType as SupportedInputTypeE,
                options: newQuestOptions,
            });
            currentTestMetaProfile.set('exitQuestions', eqs);
            setSelectedExitQuestion(eqs.length - 1);
        } else {
            eqs[selectedExitQuestion] = {
                label: newQuestLabel,
                key: eqs[selectedExitQuestion].key,
                secondaryLabel: newQuestSecondaryLabel,
                restrict: newQuestRestrict === 'true',
                qType: newQuestQType as SupportedInputTypeE,
                options: newQuestOptions,
            };
            currentTestMetaProfile.set('exitQuestions', eqs);
        }
    }

    const newExitQuestionForm = () => {

        return ([
            <Select
                label="Question Type"
                required
                placeholder="Select a question type"
                value={newQuestQType}
                onChange={setNewQuestQType}
                data={Object.entries(SupportedInputTypeEnum.enum).map(([label, value]) => ({ label, value }))}
                m={8}
            />,
            <TextInput
                label="Label"
                value={newQuestLabel}
                onChange={setNewQuestLabel}
                required m={8}
            />,
            <TextInput
                label="Help Text"
                value={newQuestSecondaryLabel}
                onChange={setNewQuestSecondaryLabel}
                m={8}
            />,
            <TagsInput
                label="Options"
                description="Separate with | or ~"
                value={newQuestOptions}
                onChange={setNewQuestOptions}
                splitChars={['|', '~']}
                m={8}
            />,
            <Group wrap="nowrap" justify="space-between" m={8}>
                <Checkbox
                    label="Restrict"
                    value={newQuestRestrict}
                    onChange={setNewQuestRestrict}
                />
                <Button onClick={addExitQuestion}>{selectedExitQuestion === -1 ? 'Add' : 'Update'}</Button>
            </Group>

        ])
    }

    const sendTestMetaProfile = () => {
        const testMetaProfile = Object.fromEntries(currentTestMetaProfile.entries()) as TestMetaProfile;
        console.log('sendTestMetaProfile', testMetaProfile);
        updateTestMetaProfile(testMetaProfile)
            .then(() => showNotification({
                title: 'Success',
                message: 'Test meta profile updated successfully',
                color: 'green',
            }))
            .catch(() => showNotification({
                title: 'Error',
                message: 'Failed to update test meta profile',
                color: 'red',
            }));
    }

    return (<Card withBorder w={{ base: '100%', md: '95%' }} mx={'auto'} mt={'md'}>
        <Group justify='space-between' wrap='nowrap'>
            <Text size={'1.4rem'} fw={300}>Test Settings</Text>
            <Group wrap="nowrap">
                {showTestMetaProfile && <Button color='yellow' onClick={sendTestMetaProfile}>Save</Button>}
                <Button onClick={() => setShowTestMetaProfile(!showTestMetaProfile)}>
                    {showTestMetaProfile ? 'Close' : 'Open'}
                    {showTestMetaProfile ? <IconChevronUp /> : <IconChevronDown />}
                </Button>
            </Group>

        </Group>
        <Collapse in={showTestMetaProfile}>
            <Divider my={8} />
            <Stack gap={12} m={8}>
                <Group wrap="nowrap" grow justify="center">
                    <Checkbox
                        label='Show Profile Card'
                        defaultChecked
                        checked={currentTestMetaProfile.get('showProfileCard') as boolean}
                        onChange={(e) => currentTestMetaProfile.set('showProfileCard', e.target.checked)}
                    />
                     <Checkbox
                        label='All Non-test questions to the end'
                        defaultChecked
                        checked={currentTestMetaProfile.get('showProfileCard') as boolean}
                        onChange={(e) => currentTestMetaProfile.set('showProfileCard', e.target.checked)}
                    />
                    <Checkbox
                        label='Show Exit Questions'
                        defaultChecked
                        checked={currentTestMetaProfile.get('showExitQuestions') as boolean}
                        onChange={(e) => currentTestMetaProfile.set('showExitQuestions', e.target.checked)}
                    />
                    <Select
                        label='Question Set'
                        defaultValue={'realestate'}
                        data={[{ label: 'Real Estate', value: 'realestate' }, { label: 'General', value: 'general' }]}
                        value={currentTestMetaProfile.get('questionSet') as string}
                        onChange={(value) => currentTestMetaProfile.set('questionSet', value as string)}
                    />
                </Group>
                <Group wrap="nowrap">
                    <Divider flex={8} label='Exit Questions' labelPosition="center" my={8} />
                    <Button disabled={selectedExitQuestion === -1} flex={1} onClick={() => setSelectedExitQuestion(-1)}>Create</Button>
                </Group>
                <Group wrap="nowrap">
                    <Box flex={1}>
                        {exitQuestionUiList()}
                    </Box>
                    <Box flex={3}>
                        <Preview
                            controls={newExitQuestionForm()}

                            minHeight={200}
                            // title={selectedExitQuestion === -1 ? 'Creating' : `Editing`}
                            code={MetaCompBlks[newQuestQType as SupportedInputTypeE || 'text'].manual}
                        >
                            <DynamicComponent
                                label={newQuestLabel}
                                key={Date.now()}
                                secondaryLabel={newQuestSecondaryLabel}
                                options={newQuestOptions}
                                restrict={newQuestRestrict === 'true'}
                                qType={newQuestQType as SupportedInputTypeE}
                            />
                        </Preview>
                    </Box>
                </Group>
                <Stack justify='space-evenly' align='center'>
                    <Divider
                        onClick={() => togglePreview()}
                        style={{ cursor: 'pointer' }}
                        label={<Group>
                            {showPreview === 1 ? <IconChevronDown /> : <IconChevronUp />}
                            <Text size='1.1rem'>Preview</Text>
                            {showPreview === 1 ? <IconChevronDown /> : <IconChevronUp />}
                        </Group>} my="md" w="100%" />
                    <Collapse in={showPreview === 1} w='100%'>
                        <DemoRoot style={{ width: '100%' }}>
                            <DemoArea
                                withPadding
                                centered
                                dimmed
                                maxWidth={600}
                                minHeight={200}
                            >
                                <DynamicForm
                                    components={currentTestMetaProfile.get('exitQuestions') as DynamicQuestionProp[]}
                                />
                            </DemoArea></DemoRoot>
                    </Collapse>
                </Stack>

            </Stack>
        </Collapse>
    </Card>)
}